import * as React from 'react';
import { HashRouter, Route, Switch } from 'react-router-dom';
import { ROUTES } from 'ts/Routes';
import { CloseLoginModal } from './login/components/close-login-modal/CloseLoginModal';
import { IFrameLoginPage } from './login/components/iframe-login/IFrameLoginPage';
import { LoginPage } from './login/components/Login/LoginPage';
import { LoginHostedPage } from './LoginHostedPage';
import { BootstrapIFrameLoginPage } from './passwordless-login/components/pages/bootstrap/BootstrapIFrameLoginPage';
import { BootstrapLoginPage } from './passwordless-login/components/pages/bootstrap/BootstrapLoginPage';
import { BootstrapRegistrationPage } from './passwordless-login/components/pages/bootstrap/BootstrapRegistrationPage';
import { BootstrapResetPasswordPage } from './passwordless-login/components/pages/bootstrap/BootstrapResetPasswordPage';
import {
  navigateToErrorPageFromIframeLogin,
  navigateToErrorPageFromLogin,
} from './passwordless-login/components/pages/error/ErrorNavigation';
import { ErrorPage } from './passwordless-login/components/pages/error/ErrorPage';
import { VersionPage } from './passwordless-login/components/pages/version/VersionPage';
import { CallCentreRegisterWizardThankyouPage } from './passwordless-login/components/wizards/call-centre-register-wizard/CallCentreRegisterWizardThankyouPage';
import { CallCentreResetPasswordWizardThankyouPage } from './passwordless-login/components/wizards/call-centre-reset-password-wizard/CallCentreResetPasswordWizardThankyouPage';
import { ForgotPasswordEnterUsernamePage } from './passwordless-login/components/wizards/forgot-password-wizard/ForgotPasswordEnterUsernamePage';
import { ForgotPasswordOneTimeCodePage } from './passwordless-login/components/wizards/forgot-password-wizard/ForgotPasswordOneTimeCodePage';
import { ForgotUsernameAccountFoundPage } from './passwordless-login/components/wizards/forgot-username-wizard/ForgotUsernameAccountFoundPage';
import { ForgotUsernameAccountNumberPage } from './passwordless-login/components/wizards/forgot-username-wizard/ForgotUsernameAccountNumberPage';
import { ForgotUsernameCustomerLookupPage } from './passwordless-login/components/wizards/forgot-username-wizard/ForgotUsernameCustomerLookupPage';
import { ManualRegAccountNumberPage } from './passwordless-login/components/wizards/manual-reg-wizard/ManualRegAccountNumberPage';
import { ManualRegCustomerLookupPage } from './passwordless-login/components/wizards/manual-reg-wizard/ManualRegCustomerLookupPage';
import { ManualRegOneTimeCodePage } from './passwordless-login/components/wizards/manual-reg-wizard/ManualRegOneTimeCodePage';
import { RegisterEnterUsernamePage } from './passwordless-login/components/wizards/register-wizard/RegisterEnterUsernamePage';
import { RegisterOneTimeCodePage } from './passwordless-login/components/wizards/register-wizard/RegisterOneTimeCodePage';
import { RegisterSetPasswordPage } from './passwordless-login/components/wizards/register-wizard/RegisterSetPasswordPage';
import { IFrameErrorPage } from './passwordless-login/components/pages/error/IFrameErrorPage';
import { ContinueWithoutLogin } from './login/components/continue-wthout-login/ContinueWithoutLogin';
import { CookiePage } from './login/components/cookie/CookiePage';
import { SharedAppContextPage } from './components/SharedAppContextPage';
import { SharedAppContextPageV2 } from './components/SharedAppContextPageV2';
import { PermissionErrorPage } from './permission/PermissionErrorPage';
import { ManualRegBusinessCustomerLookupPage } from './passwordless-login/components/wizards/manual-reg-wizard/ManualRegBusinessCustomerLookupPage';
import { ChangePasswordPage } from './change-password/ChangePasswordPage';
import { ChangePasswordSuccessPage } from './change-password/ChangePasswordSuccessPage';
import { ForgotUsernameBusinessLookupPage } from './passwordless-login/components/wizards/forgot-username-wizard/ForgotUsernameBusinessLookupPage';
import { BootstrapForgotUsernamePage } from './passwordless-login/components/pages/bootstrap/BootstrapForgotUsernamePage';

interface RouterState {
  username: string;
}

class AppRouter extends React.Component<{}, RouterState> {
  constructor(props) {
    super(props);
    this.state = {
      username: '',
    };
  }

  /**
   * Sets the username across all the routes. Username has been elevated to app level as when
   * returning (hitting back) from the error page, we want the username to be persistant.
   * Also, when we add the register route, and they navigate from login -> register
   * we would like to maintain the username
   * @param username: string
   */
  public setUsername = (username: string) => {
    this.setState({ username });
  };

  public renderNativeLoginPage = (props) => (
    <LoginPage
      username={this.state.username}
      defaultRememberMe={true}
      setUsername={this.setUsername}
      errorPageFunction={(errorCode, history) => navigateToErrorPageFromLogin(errorCode, history)}
      {...props}
    />
  );

  public renderIframeLoginPage = (props) => (
    <IFrameLoginPage
      username={this.state.username}
      defaultRememberMe={false}
      setUsername={this.setUsername}
      errorPageFunction={(errorCode, history) =>
        navigateToErrorPageFromIframeLogin(errorCode, history)
      }
      {...props}
    />
  );

  public render() {
    return (
      <HashRouter>
        <Switch>
          <Route exact path={ROUTES.ROOT} component={LoginHostedPage} />
          <Route exact path={ROUTES.ERROR} component={ErrorPage} />
          <Route exact path={ROUTES.PERMISSION_ERROR} component={PermissionErrorPage} />

          <Route exact path={ROUTES.COOKIE} component={CookiePage} />
          <Route exact path={ROUTES.SAC_TEST} component={SharedAppContextPage} />
          <Route exact path={ROUTES.SAC_TEST2} component={SharedAppContextPageV2} />

          <Route path={ROUTES.LOGIN_START} component={BootstrapLoginPage} />
          <Route exact path={ROUTES.LOGIN} render={this.renderNativeLoginPage} />

          <Route path={ROUTES.IFRAME_LOGIN_START} component={BootstrapIFrameLoginPage} />
          <Route exact path={ROUTES.IFRAME_LOGIN} component={this.renderIframeLoginPage} />
          <Route exact path={ROUTES.IFRAME_LOGIN_ERROR} component={IFrameErrorPage} />

          <Route path={ROUTES.REGISTER_START} component={BootstrapRegistrationPage} />
          <Route
            exact
            path={ROUTES.REGISTER_ENTER_USERNAME}
            component={RegisterEnterUsernamePage}
          />
          <Route path={ROUTES.REGISTER_ONE_TIME_CODE} component={RegisterOneTimeCodePage} />

          <Route path={ROUTES.MANUAL_REG_FIND_CUSTOMER} component={ManualRegCustomerLookupPage} />
          <Route
            path={ROUTES.MANUAL_REG_FIND_BUSINESS_CUSTOMER}
            component={ManualRegBusinessCustomerLookupPage}
          />
          <Route path={ROUTES.MANUAL_REG_ACCOUNT_NUMBER} component={ManualRegAccountNumberPage} />
          <Route path={ROUTES.MANUAL_REG_ONE_TIME_CODE} component={ManualRegOneTimeCodePage} />

          <Route path={ROUTES.RESET_PASSWORD_START} component={BootstrapResetPasswordPage} />
          <Route
            exact
            path={ROUTES.RESET_PASSWORD_ENTER_USERNAME}
            component={ForgotPasswordEnterUsernamePage}
          />
          <Route
            path={ROUTES.RESET_PASSWORD_ONE_TIME_CODE}
            component={ForgotPasswordOneTimeCodePage}
          />

          <Route
            path={ROUTES.FORGOT_USERNAME_FIND_BUSINESS_CUSTOMER}
            component={ForgotUsernameBusinessLookupPage}
          />
          <Route path={ROUTES.FORGOT_USERNAME_START} component={BootstrapForgotUsernamePage} />
          <Route
            path={ROUTES.FORGOT_USERNAME_FIND_CUSTOMER}
            component={ForgotUsernameCustomerLookupPage}
          />
          <Route
            path={ROUTES.FORGOT_USERNAME_ACCOUNT_NUMBER}
            component={ForgotUsernameAccountNumberPage}
          />

          <Route
            path={ROUTES.FORGOT_USERNAME_ACCOUNT_FOUND}
            component={ForgotUsernameAccountFoundPage}
          />

          <Route
            path={ROUTES.CALL_CENTRE_REGISTER_THANKYOU}
            component={CallCentreRegisterWizardThankyouPage}
          />

          <Route
            path={ROUTES.CALL_CENTRE_RESET_PASSWORD_THANKYOU}
            component={CallCentreResetPasswordWizardThankyouPage}
          />

          {/* The set password route is used only for testing purposes, this page is actually served from reset_password.ejs as an auth0 hosted page.  See ResetPasswordRouter.tsx */}
          <Route path={ROUTES.REGISTER_SET_YOUR_PASSWORD} component={RegisterSetPasswordPage} />
          <Route exact path={ROUTES.CHANGE_PASSWORD} component={ChangePasswordPage} />
          <Route
            exact
            path={ROUTES.CHANGE_PASSWORD_SUCCESS}
            component={ChangePasswordSuccessPage}
          />

          <Route path={ROUTES.VERSION} component={VersionPage} />
          <Route path={ROUTES.CLOSE_LOGIN_MODAL} component={CloseLoginModal} />
          <Route path={ROUTES.CONTINUE_WITHOUT_LOGIN} component={ContinueWithoutLogin} />
        </Switch>
      </HashRouter>
    );
  }
}

export { AppRouter };
